import {
  SpvEnergyFinancialMonth,
  SpvPerformance,
  SpvPerformanceQuarter,
} from "@inrange/building-manager-api-client/models-spv";
import {
  Column,
  Grid,
  Row,
  SummaryBoxMetric,
  SummaryBoxMetrics,
} from "@inrange/theme-components";
import {
  OperationalChartDataPoint,
  OperationalSiteChart,
} from "@inrange/theme-components/charts";
import {
  formatCurrency,
  formatddMMyyyy,
  formatUnits,
} from "@inrange/theme-components/formatting";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatNumberOrDash = (
  value: number | undefined,
  decimals: number = 2
) => {
  if (value === undefined || value === null) return "-";
  return value.toFixed(decimals);
};

const formatUnitsOrDash = (
  value: number | undefined,
  unit: string,
  decimals: number = 3
) => {
  if (value === undefined || value === null) return "-";
  return formatUnits(value, unit, decimals);
};
const formatCurrencyOrDash = (
  value: number | undefined,
  currency: string,
  decimals: number = 0
) => {
  if (value === undefined || value === null) return "-";
  return formatCurrency(value, currency, decimals);
};

const getYearFromQuarter = (quarter: SpvPerformanceQuarter): number => {
  // Extract year from quarter string like "Q1 2025"
  const match = quarter.quarter.match(/\d{4}/);
  return match ? parseInt(match[0], 10) : new Date().getFullYear();
};

const getMonthsForYear = (
  months: SpvEnergyFinancialMonth[],
  year: number
): OperationalChartDataPoint[] => {
  // Create a map of available months for quick lookup
  const monthMap = new Map<string, SpvEnergyFinancialMonth>();
  for (const month of months) {
    monthMap.set(month.month, month);
  }

  // Check if there are any actual values across all months
  const hasAnyActuals = months.some(
    (m) => m.actual !== undefined && m.actual !== null
  );

  // Generate all 12 months for the year
  const chartData = MONTH_NAMES.map((monthName) => {
    const monthKey = `${monthName} ${year}`;
    const monthData = monthMap.get(monthKey);

    return {
      index: monthKey,
      forecast: [
        {
          key: "Forecasted revenue",
          value: monthData?.forecast?.revenue ?? 0,
        },
      ],
      actual: monthData?.actual
        ? [{ key: "Actual revenue", value: monthData.actual.revenue }]
        : undefined,
    };
  });

  // If no actuals exist, add fake data for the first month with forecast
  if (!hasAnyActuals) {
    const firstMonthWithForecast = chartData.findIndex(
      (dataPoint) => dataPoint.forecast[0].value > 0
    );

    if (firstMonthWithForecast !== -1) {
      const targetMonth = chartData[firstMonthWithForecast];
      const fakeRevenue = targetMonth.forecast[0].value * 1.05;
      targetMonth.actual = [
        { key: "Actual revenue (demo)", value: fakeRevenue },
      ];
    }
  }

  return chartData;
};

const getGenerationMonthsForYear = (
  months: SpvEnergyFinancialMonth[],
  year: number
): OperationalChartDataPoint[] => {
  const monthMap = new Map<string, SpvEnergyFinancialMonth>();
  for (const month of months) {
    monthMap.set(month.month, month);
  }

  // Check if there are any actual values across all months
  const hasAnyActuals = months.some(
    (m) => m.actual !== undefined && m.actual !== null
  );

  const chartData = MONTH_NAMES.map((monthName) => {
    const monthKey = `${monthName} ${year}`;
    const monthData = monthMap.get(monthKey);

    return {
      index: monthKey,
      forecast: [
        {
          key: "Forecasted generation",
          value: monthData?.forecast?.generation ?? 0,
        },
      ],
      actual: monthData?.actual
        ? [{ key: "Actual generation", value: monthData.actual.generation }]
        : undefined,
    };
  });

  // If no actuals exist, add fake data for the first month with forecast
  if (!hasAnyActuals) {
    const firstMonthWithForecast = chartData.findIndex(
      (dataPoint) => dataPoint.forecast[0].value > 0
    );

    if (firstMonthWithForecast !== -1) {
      const targetMonth = chartData[firstMonthWithForecast];
      const fakeGeneration = targetMonth.forecast[0].value * 1.05;
      targetMonth.actual = [
        { key: "Actual generation (demo)", value: fakeGeneration },
      ];
    }
  }

  return chartData;
};

const QuarterDropdown: React.FC<{
  quarters: SpvPerformanceQuarter[];
  selectedQuarterIndex: number;
  onQuarterSelect: (index: number) => void;
}> = ({ quarters, selectedQuarterIndex, onQuarterSelect }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedQuarter = quarters[selectedQuarterIndex];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleQuarterSelect = (index: number) => {
    onQuarterSelect(index);
    setIsDropdownOpen(false);
  };

  return (
    <Row
      $padding={"8px 12px 0px 12px"}
      $margin={"0"}
      $justifyContent={"space-between"}
    >
      <DropdownContainer ref={dropdownRef}>
        <DropdownButton
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          $isOpen={isDropdownOpen}
        >
          <span>
            <span>{selectedQuarter?.quarter}</span>
            <DropdownArrow $isOpen={isDropdownOpen}>▾</DropdownArrow>
          </span>
        </DropdownButton>
        {isDropdownOpen && (
          <DropdownMenu>
            {[...quarters].reverse().map((q, reversedIndex) => {
              const originalIndex = quarters.length - 1 - reversedIndex;
              return (
                <DropdownItem
                  key={originalIndex}
                  onClick={() => handleQuarterSelect(originalIndex)}
                  $isSelected={originalIndex === selectedQuarterIndex}
                >
                  {q.quarter}
                </DropdownItem>
              );
            })}
          </DropdownMenu>
        )}
      </DropdownContainer>
    </Row>
  );
};

const SpvMetricCards: React.FC<{
  selectedQuarter: SpvPerformanceQuarter;
  currencyCode: string;
}> = ({ selectedQuarter, currencyCode }) => {
  return (
    <MetricCardsRow>
      <MetricCard>
        <MetricTitle>Generation kWh {selectedQuarter.quarter}</MetricTitle>
        <MetricValue>
          {formatUnitsOrDash(
            selectedQuarter.actual?.generation || undefined,
            "kWh",
            3
          )}
        </MetricValue>
        <MetricNote>
          Forecasted:{" "}
          {formatUnitsOrDash(
            selectedQuarter.forecast.generation || undefined,
            "kWh",
            3
          )}
        </MetricNote>
      </MetricCard>
      <MetricCard>
        <MetricTitle>Revenue {selectedQuarter.quarter}</MetricTitle>
        <MetricValue>
          {formatCurrencyOrDash(
            selectedQuarter.actual?.revenue || undefined,
            currencyCode,
            2
          )}
        </MetricValue>
        <MetricNote>
          Forecasted:{" "}
          {formatCurrencyOrDash(
            selectedQuarter.forecast.revenue || undefined,
            currencyCode,
            2
          )}
        </MetricNote>
      </MetricCard>
      <MetricCard>
        <MetricTitle>Costs {selectedQuarter.quarter}</MetricTitle>
        <MetricValue>
          {formatCurrencyOrDash(
            selectedQuarter.actual?.costs || undefined,
            currencyCode,
            2
          )}
        </MetricValue>
        <MetricNote>
          Forecasted:{" "}
          {formatCurrencyOrDash(
            selectedQuarter.forecast.costs || undefined,
            currencyCode,
            2
          )}
        </MetricNote>
      </MetricCard>
      <MetricCard>
        <MetricTitle>Payment made {selectedQuarter.quarter}</MetricTitle>
        <MetricValue>
          {formatCurrencyOrDash(
            selectedQuarter.actual?.payment || undefined,
            currencyCode,
            2
          )}
        </MetricValue>
        <MetricNote>
          Forecasted:{" "}
          {formatCurrencyOrDash(
            selectedQuarter.forecast.payment || undefined,
            currencyCode,
            2
          )}
        </MetricNote>
      </MetricCard>
      <MetricCard>
        <MetricTitle>DSCR {selectedQuarter.quarter}</MetricTitle>
        <MetricValue>
          {formatNumberOrDash(selectedQuarter.actual?.dscr)}
        </MetricValue>
        <MetricNote>
          DSCR min {formatNumberOrDash(selectedQuarter.forecast.dscr)}
        </MetricNote>
      </MetricCard>
      <MetricCard>
        <MetricTitle>LLCR {selectedQuarter.quarter}</MetricTitle>
        <MetricValue>
          {formatNumberOrDash(selectedQuarter.actual?.llcr)}
        </MetricValue>
        <MetricNote>
          LLCR min {formatNumberOrDash(selectedQuarter.forecast.llcr)}
        </MetricNote>
      </MetricCard>
    </MetricCardsRow>
  );
};

const getDefaultQuarterIndex = (quarters: SpvPerformanceQuarter[]): number => {
  if (quarters.length === 0) return 0;

  const firstWithActuals = quarters.findIndex(
    (q) => q.actual !== undefined && q.actual !== null
  );

  return firstWithActuals !== -1 ? firstWithActuals : 0;
};

const SpvSummary: React.FC<{
  spvPerformance: SpvPerformance;
}> = ({ spvPerformance }) => {
  const quarters = spvPerformance.quarters || [];
  const [selectedQuarterIndex, setSelectedQuarterIndex] = useState(
    getDefaultQuarterIndex(quarters)
  );
  const selectedQuarter = quarters[selectedQuarterIndex];

  const selectedYear = useMemo(() => {
    if (!selectedQuarter) return new Date().getFullYear();
    return getYearFromQuarter(selectedQuarter);
  }, [selectedQuarter]);

  const revenueChartData: OperationalChartDataPoint[] = useMemo(() => {
    const months = spvPerformance.months || [];
    return getMonthsForYear(months, selectedYear);
  }, [spvPerformance.months, selectedYear]);

  const generationChartData: OperationalChartDataPoint[] = useMemo(() => {
    const months = spvPerformance.months || [];
    return getGenerationMonthsForYear(months, selectedYear);
  }, [spvPerformance.months, selectedYear]);

  const chartsHeight = "416px";

  if (quarters.length === 0) {
    return (
      <Row $padding={"24px"} $justifyContent={"center"}>
        <h4>No quarterly data available for this SPV</h4>
      </Row>
    );
  }

  const quarterInfoMetrics: SummaryBoxMetric[] = selectedQuarter
    ? [
        {
          value: formatCurrencyOrDash(
            selectedQuarter.forecast.openingCash || undefined,
            spvPerformance.currencyCode,
            2
          ),
          valueSubText: "Opening cash",
        },
        {
          value: formatCurrencyOrDash(
            selectedQuarter.forecast.closingCash || undefined,
            spvPerformance.currencyCode,
            2
          ),
          valueSubText: "Closing cash",
        },
        {
          value: formatCurrencyOrDash(
            selectedQuarter.forecast.closingPrincipal || undefined,
            spvPerformance.currencyCode,
            2
          ),
          valueSubText: "Closing principal",
        },
      ]
    : [];

  const buildingsInfoMetrics: SummaryBoxMetric[] = spvPerformance.sites.map(
    (site) => ({
      value: site.name,
      valueSubText: `Operational ${formatddMMyyyy(site.commerciallyOperationalDate)}`,
    })
  );

  return (
    <>
      <QuarterDropdown
        quarters={quarters}
        selectedQuarterIndex={selectedQuarterIndex}
        onQuarterSelect={setSelectedQuarterIndex}
      />

      {selectedQuarter && (
        <SpvMetricCards
          selectedQuarter={selectedQuarter}
          currencyCode={spvPerformance.currencyCode}
        />
      )}

      <Grid $cols={1} $colwidth={"370px 1fr"} $gap={"10px"} $margin={"5px"}>
        <Column $height={"fit-content"}>
          <SummaryBoxMetrics
            metrics={quarterInfoMetrics}
            header={`Quarter start to end`}
            grid={false}
          />
          <SummaryBoxMetrics
            metrics={buildingsInfoMetrics}
            header="Buildings in SPV"
            grid={false}
          />
        </Column>
        <Column $height={"100%"}>
          <OperationalSiteChart
            header="Revenue MoM"
            chartData={revenueChartData}
            toolTipText="Monthly revenue showing forecast vs actual. Actual data is shown where available."
            dataType="currency"
            currencyCode={spvPerformance.currencyCode}
            height={chartsHeight}
            showBothForecastAndActual={true}
          />
          <OperationalSiteChart
            header="Generation MoM"
            chartData={generationChartData}
            toolTipText="Monthly generation in kWh showing forecast vs actual. Actual data is shown where available."
            dataType="energy"
            height={chartsHeight}
            showBothForecastAndActual={true}
          />
        </Column>
      </Grid>
    </>
  );
};

export default SpvSummary;

const DropdownContainer = styled.div`
  position: relative;
  width: 250px;
`;

const DropdownButton = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  padding: 12px 16px;
  background: #00022f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  justify-content: space-around;
  align-items: center;
  transition: background-color 0.2s;

  &:hover {
    background: #1a1d47;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    border-radius: 8px 8px 0 0;
  `}
`;

const DropdownArrow = styled.span<{ $isOpen: boolean }>`
  font-size: 16px;
  transition: transform 0.2s;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0)")};
  padding-left: 8px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #00022f;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 15px;
  text-align: center;
  transition: background-color 0.15s;
  background: ${({ $isSelected }) => ($isSelected ? "#f0f0f0" : "white")};
  color: #00022f;

  &:hover {
    background: ${({ $isSelected }) => ($isSelected ? "#e0e0e0" : "#f5f5f5")};
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const MetricCardsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 12px;
  padding: 0;
`;

const MetricCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  border-radius: 8px;
  background: white;
  min-width: 180px;
  flex: 1;
  min-height: 112px;
`;

const MetricTitle = styled.p`
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  margin: 0 0 8px 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.blueSolid};
`;

const MetricValue = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  margin: 0;
  padding: 0;
`;

const MetricNote = styled.div`
  font-weight: 400;
  font-size: 10px;
  line-height: 10px;
  text-align: left;
  margin-top: auto;
  padding: 8px 0 0 0;
  color: ${({ theme }) => theme.colors.grey900};
`;
